import React, { useCallback, useRef } from 'react';
import noop from '../utils/noop';
import { compose } from 'redux';
import { withRouter } from 'react-router';
var EMPTY_PATH_REGEX = "/^([^?]*?)(?:\\?([\\s\\S]*))?$/";
var BACKBONE_APP_MOCK = {
  stopSubApplication: noop
};
// Only include React routes where Backbone handles a sub-route
var REACT_ENTRY_FRAGMENTS = ['activities/tools/', 'activities/instruments/'];
var pathStripper = /#.*$/;

/**
 * Attempts to safely get Backbone.Radio channels
 * @param {String} channel
 * @param {String} request
 */
function getBackboneRadio(channel, request) {
  var radio = window.Backbone.Radio.channel(channel);
  if (!radio) {
    return {
      request: BACKBONE_APP_MOCK,
      trigger: noop
    };
  } else if (request) {
    return radio.request(request) || BACKBONE_APP_MOCK;
  } else {
    return radio;
  }
}

/**
 * This is a hodgepodge of what dm-spu-framework Router's onClickLink does AND
 * Backbone's navigate method. Want to skip the history.push step that Backbone's
 * navigate would do
 * @param {String} href
 * @this Backbone.history
 */
function doBackboneRouteMagic(href) {
  var rootURL = getBackboneRadio('framework', 'rootURL');
  if (rootURL.indexOf(rootURL) >= 0) {
    var rootPath = getBackboneRadio('framework', 'rootPath');
    var index = href.indexOf(rootPath);
    if (index >= 0) {
      var fragment = href.substring(index + rootPath.length);
      fragment = this.getFragment(fragment || '').replace(pathStripper, '');
      var decodedFragment = this.decodeFragment(fragment);
      if (this.fragment !== decodedFragment) {
        this.fragment = decodedFragment;
        this.loadUrl(fragment);
      }
    }
  }
}

/**
 * Uses Backbone.history's handlers to check if the route is known Backbone route
 * (removes the empty path from the list of handlers, though, or else everything would match)
 * src: http://backbonejs.org/docs/backbone.html#section-235
 * @param {String} href
 * @this Backbone.history
 */
function isBackboneRoute(href) {
  if (!this.matchRoot()) {
    return false;
  }
  var fragment = this.getFragment(href.split('/app/')[1]);
  return REACT_ENTRY_FRAGMENTS.indexOf(fragment) !== -1 ? false : this.handlers.filter(function (handler) {
    return handler.route.toString() !== EMPTY_PATH_REGEX;
  })
  // the callback used here by Backbone is not what we wanted, just want to make sure the
  // route matches, so no callback, just return true
  .some(function (handler) {
    return handler.route.test(fragment);
  });
}

// helper functions
var isModifiedEvent = function isModifiedEvent(event) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
};
var isNormalClick = function isNormalClick(event) {
  return event.button === 0 && !isModifiedEvent(event);
};
var withBackboneLink = function withBackboneLink(WrappedComponent) {
  return function BackboneLink(props) {
    var to = props.to,
      history = props.history;
    var listener = useRef(null);
    var handleReturnLink = useCallback(function (e) {
      try {
        var BackboneHistory = window.Backbone.history;
        if (!isNormalClick(e)) {
          return;
        }
        if (isBackboneRoute.call(BackboneHistory, to)) {
          if (listener.current && typeof listener.current === 'function') {
            listener.current();
          }
          var href = e.currentTarget.href || '';
          // So this onClick gets called before React Router runs its logic,
          // which is fine except React Router is also in charge of navigation
          // confirmation, which means we were telling Backbone "hey it's cool
          // navigate all you want" and then React Router is like "you sure
          // about that buddy?" and the user is all like "no please I want to
          // stay here" but Backbone is already going "whateva whateva I do
          // what I want!"
          // To fix this, add a listener on the history for location changes.
          // if the action is PUSH (transition confirmed), then fake Backbone's
          // magic. Otherwise, do nothing.
          listener.current = history.listen(function (location, action) {
            // if this was a confirmed navigation, navigate. Otherwise, don't.
            if (action === 'PUSH') {
              doBackboneRouteMagic.call(BackboneHistory, href);
            }
            // make sure to call unlisten
            listener.current && listener.current();
          });
        } else {
          // is React
          BackboneHistory.fragment = BackboneHistory.getFragment(to.split('/app/')[1]);
          getBackboneRadio('framework', 'application').stopSubApplication();
        }
      } catch (err) {
        // likely an error in using Backbone.history's matchRoot (from
        // `isBackboneRoute`), set `e.button` to something that isn't 0 so
        // when react-router executes its onClick handler it ignores it (like
        // it would a legacy link)
        e.button = 1;
      }
    }, [history, to]);
    return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
      onClick: handleReturnLink
    }, props));
  };
};
export default compose(withRouter, withBackboneLink);